import {
  Connection as ApiConnection,
  ConnectionType,
  ObjectStorageAuthType,
  ObjectStorageConnection,
  OAuthCredential,
  isDatabaseConfig,
  isLLMConfig,
  isBoxConfig,
  isServiceConfig,
} from '@app/shared/interfaces';
import {
  ConnectionMetadataKey,
  getConnectionMetadata,
  normalizeConnectionMetadataKey,
} from '@app/shared/utils/connection-metadata';
import { parseServerDate } from '@app/shared/helpers/date-formatting.helper';

/**
 * View model for one row of the connections list. Produced purely from the API
 * shapes ({@link ApiConnection} / {@link ObjectStorageConnection}) plus the
 * OAuth credentials needed to compute object-storage status — no component or
 * service state — so it is trivially unit-testable. The template binds the
 * precomputed fields directly (icon, description, expiry) rather than calling
 * component methods.
 */
export interface ConnectionDisplay {
  /** Stable row id, e.g. `connection_3` / `objectstorage_7`. */
  id: string;
  name: string;
  /** Normalized connection-type key (matches {@link ConnectionMetadataKey}). */
  type: ConnectionMetadataKey;
  /** One-line technical summary (host:port/db, provider/bucket, …). */
  connectionInfo: string;
  /** Display description: explicit description, else `connectionInfo`, else a placeholder. */
  description: string;
  /** Registered `svgIcon` name for `<mat-icon [svgIcon]>`, from the metadata registry. */
  svgIcon: string;
  status: 'active' | 'inactive';
  /** `true` for an object-storage connection whose OAuth credential has expired. */
  isOAuthExpired: boolean;
  host?: string;
  port?: string;
  url?: string;
  originalData: ApiConnection | ObjectStorageConnection;
}

/**
 * Builds the full display list from the three data sources, preserving the
 * de-duplication and enrichment rules the list has always applied:
 *
 *  - Object-storage connections are returned by the `/object-storage` endpoint
 *    and rendered from `objectStorage`; the matching rows in the regular
 *    `/connection` list are excluded so they don't appear twice (SCRUM-424).
 *  - `/object-storage` rows lack `name`/`description` (those live on the joined
 *    `/connection` row), so each is enriched from the connection list (SCRUM-425).
 */
export function buildConnectionDisplays(
  connections: ApiConnection[],
  objectStorage: ObjectStorageConnection[],
  oauthCredentials: OAuthCredential[],
): ConnectionDisplay[] {
  const metadataById = indexObjectStorageMetadata(connections);

  const standard = connections
    .filter((c) => normalizeConnectionMetadataKey(c.type) !== 'objectstorage')
    .map((c) => mapStandardConnectionToDisplay(c));

  const objectStorageRows = objectStorage.map((c) =>
    mapObjectStorageToDisplay(
      enrichObjectStorageWithMetadata(c, metadataById),
      oauthCredentials,
    ),
  );

  return [...standard, ...objectStorageRows];
}

/** Index the object-storage rows of the regular `/connection` list by id. */
export function indexObjectStorageMetadata(
  connections: ApiConnection[],
): Map<number, ApiConnection> {
  const map = new Map<number, ApiConnection>();
  connections.forEach((c) => {
    if (
      c.connectionId != null &&
      normalizeConnectionMetadataKey(c.type) === 'objectstorage'
    ) {
      map.set(c.connectionId, c);
    }
  });
  return map;
}

/** Maps a standard (non-object-storage) connection to its display row. */
export function mapStandardConnectionToDisplay(
  connection: ApiConnection,
): ConnectionDisplay {
  let connectionInfo = '';
  let host = '';
  let port = '';
  let url = '';

  switch (connection.type) {
    case ConnectionType.Database:
      if (isDatabaseConfig(connection.config)) {
        connectionInfo = `${connection.config.host}:${connection.config.port}/${connection.config.dbname}`;
        host = connection.config.host;
        port = connection.config.port;
      }
      break;
    case ConnectionType.DataFusion:
      // The stored url is not where queries go (EHQ-359), so it is not shown.
      connectionInfo = 'DataFusion query engine';
      break;
    case ConnectionType.LLM:
      if (isLLMConfig(connection.config)) {
        connectionInfo = `${connection.config.llmType}${connection.config.endpoint ? ` - ${connection.config.endpoint}` : ''}`;
        url = connection.config.endpoint || '';
      }
      break;
    case ConnectionType.Box:
      if (isBoxConfig(connection.config)) {
        if (connection.config.boxAppId != null) {
          const folder = connection.config.rootFolderId;
          connectionInfo =
            `Box app #${connection.config.boxAppId}` +
            (folder ? ` · folder ${folder}` : '');
        } else {
          connectionInfo = 'Legacy credentials (deprecated)';
        }
      } else {
        connectionInfo = 'Box API Connection';
      }
      break;
    case ConnectionType.Service:
      if (isServiceConfig(connection.config, connection.type)) {
        if (connection.config.globalServiceId) {
          connectionInfo = 'Global Service';
        } else {
          connectionInfo = connection.config.url || 'Custom Service';
          url = connection.config.url || '';
        }
      } else {
        connectionInfo = 'Service Connection';
      }
      break;
    default:
      connectionInfo = 'Connection';
  }

  const type = normalizeConnectionMetadataKey(connection.type);
  return {
    id: `connection_${connection.connectionId}`,
    name: connection.name,
    type,
    connectionInfo,
    description: buildDisplayDescription(connection.description, connectionInfo),
    svgIcon: getConnectionMetadata(type).svgIcon,
    status: 'active',
    isOAuthExpired: false,
    host,
    port,
    url,
    originalData: connection,
  };
}

/** Maps an (already enriched) object-storage connection to its display row. */
export function mapObjectStorageToDisplay(
  connection: ObjectStorageConnection,
  oauthCredentials: OAuthCredential[],
): ConnectionDisplay {
  const endpoint =
    connection.endpoint || connection.accountName || connection.region || '';
  const authSuffix = buildAuthSuffix(connection, oauthCredentials);
  const connectionInfo = `${connection.provider} / ${connection.bucket}${endpoint ? ` (${endpoint})` : ''}${authSuffix}`;

  return {
    id: `objectstorage_${connection.connectionId}`,
    name: connection.name,
    type: 'objectstorage',
    connectionInfo,
    description: buildDisplayDescription(connection.description, connectionInfo),
    svgIcon: getConnectionMetadata('objectstorage').svgIcon,
    status: getObjectStorageStatus(connection, oauthCredentials),
    isOAuthExpired: isObjectStorageOAuthExpired(connection, oauthCredentials),
    url: connection.endpoint || undefined,
    originalData: connection,
  };
}

/**
 * Returns an {@link ObjectStorageConnection} with `name`/`description` filled in
 * from the regular `/connection` row when the `/object-storage` endpoint omits
 * them (which it always does today — SCRUM-425). Preserves an explicit `null`
 * description from either source rather than coercing it to `undefined`, so the
 * equality short-circuit and the save payload keep their original semantics.
 */
export function enrichObjectStorageWithMetadata(
  connection: ObjectStorageConnection,
  metadataById: Map<number, ApiConnection>,
): ObjectStorageConnection {
  const meta =
    connection.connectionId != null
      ? metadataById.get(connection.connectionId)
      : undefined;
  const name = connection.name ?? meta?.name ?? '';
  const description = connection.description ?? meta?.description;
  if (connection.name === name && connection.description === description) {
    return connection;
  }
  return { ...connection, name, description };
}

/** Explicit description (trimmed), else the technical info, else a placeholder. */
function buildDisplayDescription(
  description: string | null | undefined,
  connectionInfo: string,
): string {
  const trimmed = description?.trim();
  if (trimmed) {
    return trimmed;
  }
  return connectionInfo || 'Connection details';
}

function findOAuthCredential(
  connection: ObjectStorageConnection,
  oauthCredentials: OAuthCredential[],
): OAuthCredential | undefined {
  return oauthCredentials.find(
    (c) => c.userStorageCredentialId === connection.userCredentialId,
  );
}

function isCredentialExpired(credential: OAuthCredential | undefined): boolean {
  const expiresAt = parseServerDate(credential?.expiresAt);
  return !!(expiresAt && expiresAt < new Date());
}

function buildAuthSuffix(
  connection: ObjectStorageConnection,
  oauthCredentials: OAuthCredential[],
): string {
  if (connection.authType !== ObjectStorageAuthType.OAuth) return '';
  if (!connection.userCredentialId) return ' · OAuth';

  const credential = findOAuthCredential(connection, oauthCredentials);
  if (!credential) return ' · OAuth';
  if (isCredentialExpired(credential)) return ' · OAuth (expired)';
  return ' · OAuth';
}

function getObjectStorageStatus(
  connection: ObjectStorageConnection,
  oauthCredentials: OAuthCredential[],
): 'active' | 'inactive' {
  if (connection.authType !== ObjectStorageAuthType.OAuth) return 'active';
  if (!connection.userCredentialId) return 'inactive';

  const credential = findOAuthCredential(connection, oauthCredentials);
  return isCredentialExpired(credential) ? 'inactive' : 'active';
}

function isObjectStorageOAuthExpired(
  connection: ObjectStorageConnection,
  oauthCredentials: OAuthCredential[],
): boolean {
  if (
    connection.authType !== ObjectStorageAuthType.OAuth ||
    !connection.userCredentialId
  ) {
    return false;
  }
  return isCredentialExpired(findOAuthCredential(connection, oauthCredentials));
}
